import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Flame, Plus, Trophy, TrendingUp, Calendar as CalIcon, Play, Heart } from "lucide-react";
import BrandFlame from "../components/BrandFlame";
import { Button } from "../components/ui/button";
import { LEVEL_COLORS } from "../lib/api";
import Slogan from "../components/Slogan";
import { TrialBanner } from "../components/ProGate";
import InstallPrompt from "../components/InstallPrompt";
import PushOptIn from "../components/PushOptIn";
import BuddyInvitesBanner from "../components/BuddyInvitesBanner";
import ActiveChallengesBanner from "../components/ActiveChallengesBanner";
import { toast } from "sonner";

const NUDGE_KEY = "gwg_install_nudge_shown_v1";
const DEFAULT_NUDGE_THRESHOLD = 3;

function isAppInstalled() {
  if (typeof window === "undefined") return false;
  return (
    window.navigator.standalone === true ||
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    (window.matchMedia && window.matchMedia("(display-mode: minimal-ui)").matches)
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [today, setToday] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, sched, sessions] = await Promise.all([
        api.get("/me/stats", { params: { tz_offset: -new Date().getTimezoneOffset() } }),
        (async () => {
          // LOCAL calendar date, not UTC — see CalendarPage.iso() comment.
          const now = new Date();
          const d = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
          const r = await api.get("/schedule", { params: { start: d, end: d } });
          return r.data;
        })(),
        api.get("/sessions"),
      ]);
      setStats(s.data);
      setToday(sched);
      setRecent(sessions.data.slice(0, 3));

      // Install streak nudge: once a user has logged enough workouts (threshold tuned
      // server-side via /install-config based on real retention-lift data), surface a
      // celebratory toast inviting them to add to home screen. Skipped if already
      // installed or the user has already seen+dismissed the nudge.
      if (
        !isAppInstalled() &&
        !window.localStorage.getItem(NUDGE_KEY) &&
        s.data?.total_sessions >= 1
      ) {
        let threshold = DEFAULT_NUDGE_THRESHOLD;
        try {
          const { data: cfg } = await api.get("/install-config");
          if (typeof cfg?.nudge_threshold === "number") threshold = cfg.nudge_threshold;
        } catch { /* fall back to default */ }

        if (s.data.total_sessions >= threshold) {
          window.localStorage.setItem(NUDGE_KEY, "1");
          setTimeout(() => {
            toast.success("You're crushing it. 🔥", {
              description: `${s.data.total_sessions} workouts logged — pin GYM with GRIZZ to your home screen so it's one tap away next session.`,
              duration: 12000,
              action: { label: "Install", onClick: () => nav("/install") },
              id: "install-streak-nudge-toast",
            });
          }, 1200);
        }
      }
    })();
  }, [nav]);

  const lvlColor = stats ? LEVEL_COLORS[stats.level.name] : "#FFF";
  const progress = stats ? Math.min(100, stats.level.next ? ((stats.points - stats.level.threshold) / (stats.level.next.threshold - stats.level.threshold)) * 100 : 100) : 0;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Slogan rotate={4000} testId="dashboard-slogan" />
          <h1 data-testid="dashboard-title" className="font-display text-4xl sm:text-5xl uppercase leading-none mt-1">{user?.name?.split(" ")[0] || "Athlete"}</h1>
        </div>
        <div className="flex flex-col items-center gap-1">
          <BrandFlame className="w-8 h-8" streak={stats ? stats.streak_days : null} />
          {stats && stats.streak_days > 0 && (
            <div data-testid="streak-days" className="text-[10px] font-display uppercase tracking-widest text-blue-500 whitespace-nowrap">
              {stats.streak_days} day{stats.streak_days === 1 ? "" : "s"}
            </div>
          )}
        </div>
      </div>

      <TrialBanner />
      <BuddyInvitesBanner />
      <ActiveChallengesBanner />
      <InstallPrompt />
      <PushOptIn />

      {stats && (
        <div data-testid="stats-card" className="border border-neutral-800 p-5 mb-6 fade-up">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-neutral-400 font-semibold">CURRENT LEVEL</div>
              <div className="font-display text-3xl uppercase mt-1" style={{ color: lvlColor }}>{stats.level.name}</div>
            </div>
            <Trophy className="w-10 h-10" style={{ color: lvlColor }} strokeWidth={2} />
          </div>
          <div className="mt-4">
            <div className="h-1.5 bg-neutral-800 relative">
              <div className="h-full bg-blue-500 transition-all" style={{ width: `${progress}%` }} />
            </div>
            <div className="flex justify-between text-xs text-neutral-500 mt-2 uppercase tracking-wider">
              <span>{stats.points} pts</span>
              <span>{stats.level.next ? `Next · ${stats.level.next.name}` : "Max level"}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-neutral-800">
            <Stat label="Sessions" value={stats.total_sessions} />
            <Stat label="PRs" value={stats.total_prs} />
            <Stat label="Total kg" value={Math.round(stats.total_weight_kg)} />
          </div>
        </div>
      )}

      <section className="mb-7">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl uppercase tracking-tight">Today</h2>
          <Link to="/calendar" className="text-xs uppercase tracking-widest text-neutral-400 hover:text-blue-500">View calendar</Link>
        </div>
        {today.length === 0 ? (
          <div className="border border-dashed border-neutral-800 p-8 text-center">
            <CalIcon className="w-8 h-8 text-neutral-600 mx-auto mb-3" />
            <div className="text-sm text-neutral-400">Nothing scheduled for today.</div>
            <Link to="/calendar"><Button data-testid="schedule-btn" variant="outline" className="mt-4 bg-transparent text-white border-neutral-700 rounded-none uppercase tracking-wider font-display h-9">Plan a workout</Button></Link>
          </div>
        ) : (
          <div className="space-y-2">
            {today.map((s) => (
              <div key={s.schedule_id} data-testid={`today-schedule-${s.schedule_id}`} className="flex items-center justify-between p-4 border border-neutral-800 hover:border-blue-500 transition-colors">
                <div>
                  <div className="font-display text-xl uppercase">{s.workout_name}</div>
                  <div className="text-xs text-neutral-500 uppercase tracking-wider mt-1">{s.status}</div>
                </div>
                <Link to={`/active/${s.workout_id}`}>
                  <Button className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display">
                    <Play className="w-4 h-4 mr-2" /> Start
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl uppercase tracking-tight">Quick Actions</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Link to="/workouts/new"><ActionTile icon={Plus} label="New Workout" /></Link>
          <Link to="/cardio"><ActionTile icon={Heart} label="Cardio" /></Link>
          <Link to="/library"><ActionTile icon={Flame} label="Exercises" /></Link>
          <Link to="/progress"><ActionTile icon={TrendingUp} label="Progress" /></Link>
        </div>
      </section>

      {recent.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-2xl uppercase tracking-tight mb-3">Recent Sessions</h2>
          <div className="space-y-2">
            {recent.map((r) => (
              <div key={r.session_id} className="flex items-center justify-between p-3 border border-neutral-800">
                <div>
                  <div className="text-sm font-semibold">{r.completed_at?.slice(0,10)}</div>
                  <div className="text-xs text-neutral-500 uppercase tracking-wider">{r.total_sets} sets · {r.total_reps} reps · {Math.round(r.total_weight_kg)} kg</div>
                </div>
                {r.prs?.length > 0 && <Trophy className="w-5 h-5 text-blue-500" />}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs tracking-[0.2em] uppercase text-neutral-500">{label}</div>
      <div className="font-display text-2xl mt-1">{value}</div>
    </div>
  );
}
function ActionTile({ icon: Icon, label }) {
  return (
    <div data-testid={`action-${label.toLowerCase().replace(/\s/g,'-')}`} className="border border-neutral-800 hover:border-blue-500 p-4 transition-all h-24 flex flex-col justify-between cursor-pointer">
      <Icon className="w-6 h-6 text-blue-500" strokeWidth={2.2} />
      <div className="font-display text-lg uppercase tracking-wider">{label}</div>
    </div>
  );
}
